'use client'

import { useEffect, useState } from 'react'
import { ENABLE_SYSTEM_DESIGN_INTERVIEW } from '@/lib/config'
import {
  fetchProductPacks,
  getInterviewPracticeCurrency,
  getInterviewPracticeDisplayPrice,
  type ProductPack,
} from '@/lib/productPacks'
import { formatLandingPrice, showLandingGstNote } from '@/lib/formatLandingPrice'
import styles from '../landing.module.css'
import { LandingJourneyCta } from '@/app/landing/components/LandingJourneyCta'
import { LandingRevealOnView } from '@/app/landing/components/LandingRevealOnView'

function PricingFeatures({ lines }: { lines: string[] }) {
  return (
    <ul className={styles.pricingFeatureList}>
      {lines.map((text) => (
        <li key={text} className={styles.pricingFeatureRow}>
          <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.16667 12.1667L13.0417 6.29167L11.875 5.125L7.16667 9.83333L4.79167 7.45833L3.625 8.625L7.16667 12.1667ZM8.33333 16.6667C7.18056 16.6667 6.09722 16.4479 5.08333 16.0104C4.06944 15.5729 3.1875 14.9792 2.4375 14.2292C1.6875 13.4792 1.09375 12.5972 0.65625 11.5833C0.21875 10.5694 0 9.48611 0 8.33333C0 7.18056 0.21875 6.09722 0.65625 5.08333C1.09375 4.06944 1.6875 3.1875 2.4375 2.4375C3.1875 1.6875 4.06944 1.09375 5.08333 0.65625C6.09722 0.21875 7.18056 0 8.33333 0C9.48611 0 10.5694 0.21875 11.5833 0.65625C12.5972 1.09375 13.4792 1.6875 14.2292 2.4375C14.9792 3.1875 15.5729 4.06944 16.0104 5.08333C16.4479 6.09722 16.6667 7.18056 16.6667 8.33333C16.6667 9.48611 16.4479 10.5694 16.0104 11.5833C15.5729 12.5972 14.9792 13.4792 14.2292 14.2292C13.4792 14.9792 12.5972 15.5729 11.5833 16.0104C10.5694 16.4479 9.48611 16.6667 8.33333 16.6667Z" fill="#34C759" />
          </svg>

          <span>{text}</span>
        </li>
      ))}
    </ul>
  )
}

function PriceDisplay({
  amount,
  currency,
}: {
  amount: number | undefined
  currency: string
}) {
  if (amount == null) return null
  return (
    <>
      <p className={styles.pricingAmount}>{formatLandingPrice(amount, currency)}</p>
      {showLandingGstNote(currency) ? (
        <p className={styles.pricingGst}>+ GST</p>
      ) : null}
    </>
  )
}

export function LandingPricing() {
  const [packs, setPacks] = useState<ProductPack[]>([])
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false

    void fetchProductPacks()
      .then((data) => {
        if (!cancelled) setPacks(data)
      })
      .catch(() => {
        // Prices stay hidden when the catalog endpoint is unavailable.
      })
      .finally(() => {
        if (!cancelled) setReady(true)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const currency = ready ? getInterviewPracticeCurrency(packs) : 'INR'
  const mockPrice = ready
    ? getInterviewPracticeDisplayPrice(packs, 'skill_assessment')
    : undefined
  const jdPrice = ready ? getInterviewPracticeDisplayPrice(packs, 'jd_prep') : undefined
  const systemDesignPrice = ready
    ? getInterviewPracticeDisplayPrice(packs, 'system_design')
    : undefined

  return (
    <section className={styles.pricingSection} aria-labelledby="pricing-heading">
      <LandingRevealOnView className={styles.pricingHeader}>
        <span className={styles.pricingChip}>Pricing</span>
        <h2 id="single-plans-heading" className={styles.singlePlansHeading}>
          Choose Your Interview Format
        </h2>
        <p className={styles.pricingSubtitle}>
          Real interview experience. Real feedback. Real growth.
        </p>
      </LandingRevealOnView>
      <div className={styles.pricingCards}>
        <LandingRevealOnView as="article" className={styles.pricingCard}>
          <div className={styles.pricingCardInner}>
            <div className={styles.pricingCardHeaderWrapper}>
              <span className={styles.pricingBadgeGreen}>Quick Start</span>
              <div className={styles.pricingCardHead}>
                <p className={styles.pricingTierLabel}>Mock Interview</p>
                <div>
                  <PriceDisplay amount={mockPrice} currency={currency} />
                </div>
              </div>
            </div>
            <PricingFeatures
              lines={[
                'CV-based personalized questions',
                'Real interview-like experience',
                'Detailed performance feedback',
                'Improve confidence & communication',
                'Quick, on demand mock interview practice',
              ]}
            />
          </div>
          <LandingJourneyCta journey="resume" ctaSurface="pricing" className={styles.pricingCtaOutline}>
            Start Practicing
          </LandingJourneyCta>
        </LandingRevealOnView>

        <LandingRevealOnView
          as="article"
          className={`${styles.pricingCard} ${styles.pricingCardFeatured}`}
          delayMs={120}
        >
          <div className={styles.pricingCardInner}>
            <div className={styles.pricingCardHeaderWrapper}>
              <span className={styles.pricingBadgePopular}>Most Popular</span>
              <div className={styles.pricingCardHead}>
                <p className={styles.pricingTierLabel}>JD Interview</p>
                <div>
                  <PriceDisplay amount={jdPrice} currency={currency} />
                </div>
              </div>
            </div>
            <PricingFeatures
              lines={[
                'Questions tailored to the job description',
                'Role-specific skill evaluation',
                'Real-world interview scenarios',
                'Actionable feedback for improvement',
                'Align preparation with company expectations',
              ]}
            />
          </div>
          <LandingJourneyCta journey="jd" ctaSurface="pricing" className={styles.pricingCtaOutline}>
            Start Practicing
          </LandingJourneyCta>
        </LandingRevealOnView>

        <LandingRevealOnView
          as="article"
          className={
            ENABLE_SYSTEM_DESIGN_INTERVIEW
              ? styles.pricingCard
              : `${styles.pricingCard} ${styles.pricingCardComingSoon}`
          }
          delayMs={240}
        >
          <div className={styles.pricingCardInner}>
            <div className={styles.pricingCardHeaderWrapper}>
              {ENABLE_SYSTEM_DESIGN_INTERVIEW ? (
                <span className={styles.pricingBadgeGreen}>Quick Start</span>
              ) : (
                <span className={styles.pricingBadgeComingSoon}>Coming Soon</span>
              )}
              <div className={styles.pricingCardHead}>
                <p className={styles.pricingTierLabel}>System Design Interview</p>
                <div>
                  <PriceDisplay amount={systemDesignPrice} currency={currency} />
                </div>
              </div>
            </div>
            <PricingFeatures
              lines={[
                'Solve real-world system design problems',
                'Practice scalability & architecture thinking',
                'Explain and defend your approach',
                'Structured feedback on design decisions',
                'Ideal for senior-level interviews',
              ]}
            />
          </div>
          {ENABLE_SYSTEM_DESIGN_INTERVIEW ? (
            <LandingJourneyCta journey="system-design" ctaSurface="pricing" className={styles.pricingCtaOutline}>
              Start Practicing
            </LandingJourneyCta>
          ) : (
            <span className={styles.pricingComingSoonCta} aria-disabled="true">
              Start Practicing
            </span>
          )}
        </LandingRevealOnView>
      </div>
    </section>
  )
}
